import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import theme from '@/styles/theme';
import validator from '@/constant/valitator';
import useAlert from '@/hooks/use-alert';
import { actions } from '@/store';
import Message from '@/constant/message';
import { EAuthModalPage } from '@/store/types';
import postResetPasswordAfterVerifyEmail from '@/fetches/user/auth/post-reset-password-after-verify-email';
import timeSleep from '@/utils/time-sleep';
import LoadingSpinner from '@/components/pages/user/common/loading-spinner';
import useSelectorUser from '@/hooks/use-selector-user';
import media from '@/styles/media';
import { Container, ContentDiv, Wrapper } from '../common/new-auth-container';
import {
  BasicButton, BasicButtonLabel, ButtonDiv,
} from '../common/new-auth-button';
import { Input } from '../common/new-auth-input';
import { Title } from '../common/new-auth-text';
import { BalloonModal } from '../common/balloon-modal';
import MobileLogo from '../common/auth-layout/mobile-logo';

export const ResetPassword = () => {
  const { gotoAuthRegister, pageType } = useSelectorUser('authModal');
  const [pw, setPw] = useState<string>('');
  const [confirmPw, setConfirmPw] = useState<string>('');

  const [isPw, setIsPw] = useState<boolean>(false);
  const [pwModal, setPwModal] = useState<string>('');

  const [isConfirm, setIsConfirm] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { alert } = useAlert();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onClickRegisterButton = useCallback(() => {
    if (gotoAuthRegister) {
      gotoAuthRegister();
    }
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SELECT_USER));
  }, [dispatch, gotoAuthRegister]);

  const validatePassword = useCallback((value: string) => {
    const { err, args } = validator.checkPassword(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      if (args?.valueKeys) {
        return t(err, { value: args.valueKeys.map((v) => t(v)).join('\n') }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const onChangePwInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validatePassword(value);
    setPw(value);
    if (value.length >= 3) {
      setIsPw(valid === '');
      if (valid !== '') { setPwModal(valid); }
    } else if (value.length < 1) {
      setPwModal('');
    }
  }, [validatePassword]);

  const onFocusOutPw = useCallback(() => {
    setPwModal('');
  }, []);

  const onChangeConfirmInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setConfirmPw(value);
    if (value.length > 0) { setIsConfirm(true); }
  }, []);

  const onFocusOutConfirm = useCallback(() => {
    setIsConfirm(false);
  }, []);

  const onClickButton = useCallback(async () => {
    setIsLoading(true);
    await timeSleep(3000);
    const result = await postResetPasswordAfterVerifyEmail(pw);
    if (result) {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.COMPLETE_PASSWORD_MOBILE));
    } else {
      await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    }
    setIsLoading(false);
  }, [alert, dispatch, pw, t]);

  const handleOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onClickButton();
    }
  }, [onClickButton]);

  return (
    <Wrapper>
      <Header>
        <BasicButtonLabel>
          {`${t(Message.계정이없으신가요)}`}
        </BasicButtonLabel>
        <MobileLogo />
        <BasicButton
          onClick={onClickRegisterButton}
          className="signup-reset"
        >
          {t(Message.회원가입)}
        </BasicButton>
      </Header>
      <ContentDiv>
        { pageType === 'reset' ? (
          <ResetTitle>
            {t(Message.비밀번호재설정)}
            {pageType && <TilteSubText>{t(Message.보다안전한서비스이용을위해비밀번호를재설정해주세요)}</TilteSubText>}
          </ResetTitle>
        ) : <Title>{t(Message.비밀번호재설정)}</Title>}
        <Input
          onChange={onChangePwInput}
          placeholder={t(Message.새로운비밀번호를입력해주세요)}
          validate={isPw}
          type="password"
          onBlur={onFocusOutPw}
        />
        {pwModal !== '' && <BalloonModal fade={isPw} text={pwModal} />}
        <Input
          onChange={onChangeConfirmInput}
          placeholder={t(Message.새로운비밀번호를입력하여재설정을완료해주세요)}
          type="password"
          validate={confirmPw === pw}
          onKeyPress={handleOnKeyPress}
          onBlur={onFocusOutConfirm}
        />
        {isConfirm && <BalloonModal fade={confirmPw === pw} text={t(Message.입력한두비밀번호가다릅니다)} />}
        <ButtonContainer
          className="reset-password"
        >
          <Button
            disabled={(isPw === false || confirmPw !== pw) && !isLoading}
            type="button"
            loading={isLoading}
            onClick={onClickButton}
          >
            {isLoading ? (
              <LoadingSpinner />
            ) : t(Message.확인)}
          </Button>
        </ButtonContainer>

      </ContentDiv>
    </Wrapper>

  );
};

const Header = styled(ButtonDiv)`
    padding-bottom: 60px;
`;

interface ButtonProps {
  loading?: boolean;
}

const Button = styled(BasicButton)<ButtonProps>`
  height: 50px;
  flex: 1;
  padding: 16px;
  background-color: ${theme.color.purple};
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled{
    background-color: ${({ loading }) => (loading ? theme.color.purple : '#F0F0F0')};
    color: ${theme.color.darkGray};
  }
`;

const ButtonContainer = styled(Container)`
  padding-top: 172px;
  &.reset-password {
    padding-top: 100px;
    max-width: 420px;
    width: 100%;
  }
`;

const TilteSubText = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 22px;
  ${media.lg`
    font-family: ${theme.fontFamily.medium};
    text-align: center;
    padding-bottom: 22px;
  `}
`;

const ResetTitle = styled(Title)`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-bottom: 12px;
`;
